import { useState, useEffect, useRef } from 'react';
import { Inmueble, InmuebleEstado } from '@/types';
import inmuebleService from '@/services/inmuebleService';
import Input from './Input';
import { getClientFullName } from '@/utils/clientUtils';

type Props = {
  selectedInmueble: Inmueble | null;
  onInmuebleSelect: (inmueble: Inmueble | null) => void;
  label?: string;
  filterEstado?: InmuebleEstado;
  disabled?: boolean;
};

export default function InmuebleSearchCard({
  selectedInmueble,
  onInmuebleSelect,
  label = 'Inmueble',
  filterEstado,
  disabled = false
}: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Inmueble[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const trimmedSearchTerm = searchTerm.trim();
  const canSearch = /^\d$/.test(trimmedSearchTerm) || trimmedSearchTerm.length >= 2;

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!trimmedSearchTerm || !canSearch) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await inmuebleService.buscarInmuebles(trimmedSearchTerm);

        // Backend returns Response::success() which wraps data in { data: [...] }
        const inmuebles: Inmueble[] = Array.isArray(response.data) ? response.data : [];

        // Filter by estado if specified
        const filteredInmuebles = filterEstado
          ? inmuebles.filter((i: Inmueble) => i.estado === filterEstado)
          : inmuebles;

        setSearchResults(filteredInmuebles);
        setShowResults(true);
      } catch (error) {
        console.error('Error searching inmuebles:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 300); // Debounce

    return () => clearTimeout(timer);
  }, [trimmedSearchTerm, canSearch, filterEstado]);

  const handleSelect = (inmueble: Inmueble) => {
    onInmuebleSelect(inmueble);
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleClear = () => {
    onInmuebleSelect(null);
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  const getStatusColor = (estado: string) => {
    const colors: Record<string, string> = {
      [InmuebleEstado.LIBRE]: 'bg-green-100 text-green-800',
      [InmuebleEstado.ALQUILADO]: 'bg-blue-100 text-blue-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div ref={containerRef} className="border rounded-lg p-4 bg-white">
      <label className="block text-sm font-medium mb-2">
        {label}
      </label>

      {selectedInmueble ? (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="font-medium">
                {selectedInmueble.direccion}
              </p>
              <p className="text-sm text-gray-600">
                {selectedInmueble.ciudad}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(selectedInmueble.estado)}`}>
                  {selectedInmueble.estado}
                </span>
              </div>

              {/* Show propietarios if available */}
              {selectedInmueble.propietarios && selectedInmueble.propietarios.length > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-300">
                  <p className="text-xs font-medium text-blue-900 mb-1">
                    Propietario{selectedInmueble.propietarios.length > 1 ? 's' : ''}:
                  </p>
                  <div className="space-y-1">
                    {selectedInmueble.propietarios.map((prop) => (
                      <p key={prop.id} className="text-xs text-blue-800">
                        • {getClientFullName(prop)}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {!disabled && (
              <button
                onClick={handleClear}
                className="text-red-600 hover:text-red-800 text-sm font-medium ml-3"
              >
                Quitar
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="relative">
          <Input
            value={searchTerm}
            onChange={(e) => !disabled && setSearchTerm(e.target.value)}
            placeholder={disabled ? 'Inmueble preseleccionado' : 'Buscar por dirección, ciudad o padrón...'}
            disabled={disabled}
          />

          {loading && (
            <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-white border rounded shadow-sm text-sm text-gray-600 z-10">
              Buscando...
            </div>
          )}

          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-64 overflow-y-auto z-10">
              {searchResults.map((inmueble) => (
                <button
                  key={inmueble.id}
                  onClick={() => handleSelect(inmueble)}
                  className="w-full text-left p-3 hover:bg-gray-50 border-b last:border-b-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium">
                        {inmueble.direccion}
                      </p>
                      <p className="text-sm text-gray-600">
                        {inmueble.ciudad}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(inmueble.estado)} ml-2`}>
                      {inmueble.estado}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {showResults && searchResults.length === 0 && canSearch && !loading && (
            <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-white border rounded shadow-sm z-10">
              <p className="text-sm text-gray-600">No se encontraron inmuebles</p>
              <p className="text-xs text-gray-500 mt-1">
                {filterEstado ? `Buscando solo inmuebles con estado: ${filterEstado}` : 'Prueba con otro término de búsqueda'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
